/**
 * A SUGGESTED supplier number derived from the document's detected issuer
 * name — never authoritative. The number is the org's own ERP identity, so
 * SupplierAssignControl's create form always keeps it editable; this only
 * saves the reviewer from typing one when a sane default exists. The 409
 * SUPPLIER_NUMBER_TAKEN branch stays the authoritative guard (a race, or a
 * match this suggestion's own candidate list did not carry, still lands
 * there) — `existingNumbers` below only avoids the PREDICTABLE collision:
 * suggesting the exact number a search the reviewer already ran just showed
 * them.
 *
 * Transliterates German umlauts/ß BEFORE stripping (never drops them
 * silently — repo convention: "Slug generators that drop umlauts →
 * Transliterate ü→ue, ö→oe, ä→ae, ß→ss"), then keeps [A-Z0-9] only,
 * uppercases, and truncates to 12 characters (already ASCII by that point,
 * so a plain `.slice` cannot split a multi-byte character).
 *
 * THE SUFFIX. A static "-1" guarantees a repeat 409 for a returning issuer:
 * once "BUCHERHYDRAU-1" exists (an earlier declaration from the same
 * supplier), every later declaration from them re-suggests the SAME taken
 * number. `existingNumbers` — the caller's own search-step results, the
 * candidates it already fetched to decide there was no match to assign
 * instead — lets this walk the suffix past whatever is already taken, so the
 * common "reviewer just searched this exact name, found nothing exact,
 * clicked add new" path suggests a number nobody holds yet.
 */
const UMLAUT_MAP: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  ß: "ss",
  Ä: "AE",
  Ö: "OE",
  Ü: "UE",
}

function transliterateGerman(input: string): string {
  return input.replace(/[äöüßÄÖÜ]/g, (ch) => UMLAUT_MAP[ch] ?? ch)
}

// Unicode combining diacritical marks (U+0300–U+036F) — what NFKD
// decomposition leaves behind on other accented Latin letters (é → e + ´),
// stripped so e.g. "Café" degrades to "CAFE" rather than vanishing entirely.
const COMBINING_MARKS = /[\u0300-\u036f]/g

const MAX_BASE_LENGTH = 12
const FALLBACK_BASE = "LIEFERANT"

export function suggestSupplierNumber(
  name: string,
  existingNumbers: readonly string[] = [],
): string {
  const alnum = transliterateGerman(name)
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
  const base = alnum.slice(0, MAX_BASE_LENGTH) || FALLBACK_BASE
  const taken = new Set(existingNumbers.map((n) => n.toUpperCase()))
  let suffix = 1
  while (taken.has(`${base}-${suffix}`)) suffix += 1
  return `${base}-${suffix}`
}
